import type { RefObject } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";

import styles from "./ApplicationMenu.module.css";

type OpenMenu = "file" | "edit" | null;

interface ApplicationMenuProps {
  readonly fileInput: RefObject<HTMLInputElement>;
  readonly hasWorkspaceContent: boolean;
  readonly canSave: boolean;
  readonly savingZip: boolean;
  readonly onFilesSelected: (files: readonly File[]) => void;
  readonly onChooseFiles: () => void;
  readonly onClearWorkspace: () => void;
  readonly onSaveZip: () => void;
  readonly onShowQuickSearch: () => void;
}

function filesFromList(list: FileList | null): readonly File[] {
  return list === null ? [] : Array.from(list);
}

export function ApplicationMenu({
  fileInput,
  hasWorkspaceContent,
  canSave,
  savingZip,
  onFilesSelected,
  onChooseFiles,
  onClearWorkspace,
  onSaveZip,
  onShowQuickSearch,
}: ApplicationMenuProps) {
  const menus = useRef<HTMLDivElement>(null);
  const firstFileMenuItem = useRef<HTMLButtonElement>(null);
  const firstEditMenuItem = useRef<HTMLButtonElement>(null);
  const menuOpen = useSignal<OpenMenu>(null);

  const runMenuAction = (action: () => void): void => {
    menuOpen.value = null;
    action();
  };

  const toggleMenu = (menu: Exclude<OpenMenu, null>): void => {
    const opening = menuOpen.value !== menu;
    menuOpen.value = opening ? menu : null;
    if (opening) {
      queueMicrotask(() => {
        (menu === "file"
          ? firstFileMenuItem.current
          : firstEditMenuItem.current
        )?.focus();
      });
    }
  };

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (
        target instanceof Node &&
        menus.current !== null &&
        !menus.current.contains(target)
      ) {
        menuOpen.value = null;
      }
    };

    const handleKeyboard = (event: KeyboardEvent): void => {
      const commandKey = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      if (
        event.key === "Escape" ||
        (commandKey && key === "o") ||
        (commandKey && event.shiftKey && key === "f")
      ) {
        menuOpen.value = null;
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyboard);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyboard);
    };
  }, []);

  return (
    <nav class={styles.menuBar} aria-label="Application menu">
      <div ref={menus} class={styles.menuGroup}>
        <div class={styles.menuContainer}>
          <button
            type="button"
            class={`${styles.menuButton} ${menuOpen.value === "file" ? styles.menuButtonOpen : ""}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen.value === "file"}
            onClick={() => toggleMenu("file")}
          >
            File
          </button>
          {menuOpen.value === "file" ? (
            <div class={styles.menu} role="menu" aria-label="File">
              <button
                ref={firstFileMenuItem}
                type="button"
                role="menuitem"
                class={styles.menuItem}
                onClick={() => runMenuAction(onChooseFiles)}
              >
                <span>Open File...</span>
                <kbd>Ctrl+O</kbd>
              </button>
              <button
                type="button"
                role="menuitem"
                class={styles.menuItem}
                disabled={!hasWorkspaceContent}
                onClick={() => runMenuAction(onClearWorkspace)}
              >
                <span>Close All</span>
              </button>
              <div class={styles.menuSeparator} role="separator" />
              <button
                type="button"
                role="menuitem"
                class={styles.menuItem}
                disabled={!canSave || savingZip}
                onClick={() => runMenuAction(onSaveZip)}
              >
                <span>Save decompiled zip</span>
              </button>
            </div>
          ) : null}
        </div>

        <div class={styles.menuContainer}>
          <button
            type="button"
            class={`${styles.menuButton} ${menuOpen.value === "edit" ? styles.menuButtonOpen : ""}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen.value === "edit"}
            onClick={() => toggleMenu("edit")}
          >
            Edit
          </button>
          {menuOpen.value === "edit" ? (
            <div class={styles.menu} role="menu" aria-label="Edit">
              <button
                ref={firstEditMenuItem}
                type="button"
                role="menuitem"
                class={styles.menuItem}
                onClick={() => runMenuAction(onShowQuickSearch)}
              >
                <span>Quick Search</span>
                <kbd>Ctrl+Shift+F</kbd>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <span class={styles.jdkVersion}>
        jd-core-js v0.0.2 / OpenJDK: 26.0.1
      </span>
      <input
        ref={fileInput}
        class={styles.fileInput}
        type="file"
        multiple
        onChange={(event) => {
          onFilesSelected(filesFromList(event.currentTarget.files));
          event.currentTarget.value = "";
        }}
      />
    </nav>
  );
}
